import { CAPACITY_DECLINE_MODE, CAPACITY_MAX_DAYS } from "core/consts";
import { getReasonTranslation } from "core/model/auctions";
import { addDaysUnixUtc } from "core/model/utils/dates";
import {
  Capacity,
  Careprovider,
  GetOntologyType,
  NoCapacityReason,
} from "core/types";
import { fromUnixTime, getUnixTime, startOfDay } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import Translations from "translations/types";
import { z } from "zod";

export function getNoCapacityReasonsMessage({
  getOntology,
  reasons = [],
  translations,
}: {
  getOntology: GetOntologyType;
  reasons: Array<NoCapacityReason> | undefined;
  translations: Translations;
}): string {
  return Array.isArray(reasons)
    ? reasons
        .map(
          (reason) =>
            Array.isArray(reason.values) &&
            reason.values
              .map((reasonValue: number) =>
                getReasonTranslation({
                  getOntology,
                  reason: reason.type,
                  translations,
                  value: reasonValue,
                }),
              )
              .join(", "),
        )
        .join(", ")
    : "";
}

export function setTimeToMidnight(capacityDate: number) {
  return getUnixTime(
    zonedTimeToUtc(startOfDay(fromUnixTime(capacityDate)), "UTC"),
  );
}

// The capacity radio is rendered with the ds/ui ControlledRadioTileGroup, which
// is backed by HeroUI and works with string values only. We therefore keep
// has_capacity as a "true" | "false" string in the form and convert to/from the
// backend boolean at the mapping boundary below.
export const HAS_CAPACITY = "true";
export const HAS_NO_CAPACITY = "false";

export const getCapacitySettingsSchema = (translations: Translations) =>
  z
    .object({
      available_date: z.number().nullable(),
      decline_scope: z.union([
        z.literal(CAPACITY_DECLINE_MODE.KEEP_CURRENT_REQUESTS),
        z.literal(CAPACITY_DECLINE_MODE.DECLINE_CURRENT_REQUESTS),
      ]),
      has_capacity: z.union([
        z.literal(HAS_CAPACITY),
        z.literal(HAS_NO_CAPACITY),
      ]),
    })
    .superRefine((values, ctx) => {
      // Date is only relevant when the provider has no capacity.
      if (values.has_capacity === HAS_CAPACITY) return;

      const timestampCurrent = addDaysUnixUtc(0, { endOfDay: true });
      const timestampCapacityMax = addDaysUnixUtc(CAPACITY_MAX_DAYS, {
        endOfDay: true,
      });
      const { available_date } = values;

      if (
        available_date == null ||
        available_date > timestampCapacityMax ||
        available_date < timestampCurrent
      ) {
        ctx.addIssue({
          code: "custom",
          message:
            translations.careproviderApp.settings.capacity.tileNoCapacity
              .errorDateInPast,
          path: ["available_date"],
        });
      }
    });

export type CapacityFormValues = z.infer<
  ReturnType<typeof getCapacitySettingsSchema>
>;

export const getCapacityDefaultValues = (
  careprovider: Careprovider,
): CapacityFormValues => ({
  available_date: careprovider.capacity?.available_date ?? null,
  decline_scope:
    careprovider.capacity?.decline_scope ??
    CAPACITY_DECLINE_MODE.DECLINE_CURRENT_REQUESTS,
  // Default to "has capacity" unless the stored value is explicitly false.
  has_capacity:
    careprovider.capacity?.has_capacity !== false
      ? HAS_CAPACITY
      : HAS_NO_CAPACITY,
});

export const mapFormValuesToCapacity = (
  values: CapacityFormValues,
): Omit<Capacity, "capacity_confirmed"> => {
  // When the provider has capacity, clear the no-capacity fields.
  if (values.has_capacity === HAS_CAPACITY) {
    return {
      available_date: null,
      decline_scope: null,
      has_capacity: true,
    };
  }

  return {
    available_date:
      values.available_date != null
        ? setTimeToMidnight(values.available_date)
        : null,
    decline_scope:
      values.decline_scope ?? CAPACITY_DECLINE_MODE.DECLINE_CURRENT_REQUESTS,
    has_capacity: false,
  };
};
